import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, 
    query, 
    where, 
    onSnapshot, 
    doc, 
    updateDoc 
} from '../../../lib/auditedFirestore';
import { httpsCallable } from 'firebase/functions'; 
import { db, functions } from '../../../lib/firebase'; 
import Spinner from '../../../components/Spinner';
import {filterStudents} from '../../attendance/studentDirectory';

// --- Individual Assign Papers Modal ---
const AssignPapersModal = ({ student, onClose, setModal }) => {
    const [papers, setPapers] = useState([]);
    const [assignedPapers, setAssignedPapers] = useState(student.assignedPapers || []);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const q = query(collection(db, "papers"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const papersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPapers(papersData);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleTogglePaper = (paperId) => {
        setAssignedPapers(prev =>
            prev.includes(paperId) ? prev.filter(id => id !== paperId) : [...prev, paperId]
        );
    };

    const handleSave = async () => {
        try {
            const userRef = doc(db, 'users', student.id);
            await updateDoc(userRef, { assignedPapers });
            setModal({ show: true, message: 'Student papers updated successfully.'});
            onClose();
        } catch (error) {
            console.error("Error updating papers:", error);
            setModal({ show: true, message: `Error: ${error.message}`});
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-2xl max-w-lg w-full">
                <h3 className="text-xl font-bold mb-4">Assign Papers for {student.email}</h3>
                {isLoading ? <Spinner color="border-purple-500" /> : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {papers.map(paper => (
                            <label key={paper.id} className="flex items-center p-2 rounded hover:bg-gray-100 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={assignedPapers.includes(paper.id)}
                                    onChange={() => handleTogglePaper(paper.id)}
                                    className="h-4 w-4 rounded text-purple-600 focus:ring-purple-500 cursor-pointer"
                                />
                                <span className="ml-3">{paper.name}</span>
                            </label>
                        ))}
                    </div>
                )}
                <div className="flex justify-end gap-4 mt-6">
                    <button onClick={onClose} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSave} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700">Save</button>
                </div>
            </div>
        </div>
    );
};

// --- Bulk Remove Papers Modal ---
const BulkRemovePapersModal = ({ selectedUserIds, users, allPapers, onClose, setModal, clearSelection }) => {
    const [papersToRemove, setPapersToRemove] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);

    const handleTogglePaper = (paperId) => {
        setPapersToRemove(prev => 
            prev.includes(paperId) ? prev.filter(id => id !== paperId) : [...prev, paperId]
        );
    };

    const handleSave = async () => {
        if (papersToRemove.length === 0) {
            onClose();
            return;
        }
        
        setIsUpdating(true);
        try {
            const updatePromises = selectedUserIds.map(userId => {
                const user = users.find(u => u.id === userId);
                if (!user) return Promise.resolve();
                
                const currentPapers = user.assignedPapers || [];
                const newPapers = currentPapers.filter(paperId => !papersToRemove.includes(paperId));
                
                const userRef = doc(db, 'users', userId);
                return updateDoc(userRef, { assignedPapers: newPapers });
            });

            await Promise.all(updatePromises);
            setModal({ show: true, message: `Successfully removed selected papers from ${selectedUserIds.length} students.`});
            clearSelection();
            onClose();
        } catch (error) {
            console.error("Error bulk removing papers:", error);
            setModal({ show: true, message: `Error: ${error.message}`});
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-2xl max-w-lg w-full">
                <h3 className="text-xl font-bold mb-2">Bulk Remove Papers</h3>
                <p className="text-sm text-gray-600 mb-4">
                    Select the papers you want to <strong>remove</strong> from the {selectedUserIds.length} selected students.
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                    {allPapers.map(paper => (
                        <label key={paper.id} className="flex items-center p-2 rounded hover:bg-red-50 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={papersToRemove.includes(paper.id)}
                                onChange={() => handleTogglePaper(paper.id)}
                                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                            />
                            <span className="ml-3 text-gray-800">{paper.name}</span>
                        </label>
                    ))}
                </div>
                <div className="flex justify-end gap-4 mt-6">
                    <button onClick={onClose} disabled={isUpdating} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50">Cancel</button>
                    <button onClick={handleSave} disabled={isUpdating} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50">
                        {isUpdating ? 'Removing...' : 'Remove Selected Papers'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Main UserList Component ---
const UserList = ({ roleToList, setModal, onEditUser }) => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [managingStudent, setManagingStudent] = useState(null);
    const [showBulkRemoveModal, setShowBulkRemoveModal] = useState(false);
    
    // Filters
    const [nameFilter, setNameFilter] = useState('');
    const [paperFilter, setPaperFilter] = useState('');
    const [allPapers, setAllPapers] = useState([]);
    const [sort, setSort] = useState('name-asc');
    const [page, setPage] = useState(0);
    const [loadError, setLoadError] = useState('');
    
    // Bulk Delete State
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);

    // 1. Fetch all papers for filter dropdown & bulk removal
    useEffect(() => {
        if (roleToList === 'student') {
            const q = query(collection(db, "papers"));
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const papersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAllPapers(papersData);
            });
            return () => unsubscribe();
        }
    }, [roleToList]);

    // 2. Fetch users based on role
    useEffect(() => {
        setIsLoading(true);setUsers([]);setSelectedUsers([]);setLoadError('');
        const q = query(collection(db, "users"), where("role", "==", roleToList));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const usersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUsers(usersData);
            setIsLoading(false);
        }, error => {setLoadError(error.message || 'Could not load users.');setIsLoading(false);});
        return () => unsubscribe();
    }, [roleToList]);

    const filteredUsers = useMemo(() => filterStudents(users.filter(user =>
        roleToList!=='student' || !paperFilter || (paperFilter==='__none__' ? !(user.assignedPapers || []).length : (user.assignedPapers || []).includes(paperFilter))
    ), {search:nameFilter,sort}), [users,nameFilter,paperFilter,sort,roleToList]);
    const pages=Math.max(1,Math.ceil(filteredUsers.length/25)),currentPage=Math.min(page,pages-1);
    const visibleUsers=filteredUsers.slice(currentPage*25,(currentPage+1)*25);
    useEffect(()=>{setPage(0);setSelectedUsers([]);},[nameFilter,paperFilter,roleToList]);
    useEffect(()=>{setPage(0);},[sort]);
    useEffect(()=>{setSelectedUsers(previous=>previous.filter(id=>users.some(user=>user.id===id)));},[users]);

    // 4. Bulk Select/Delete Logic
    const handleSelectUser = (userId) => {
        setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUsers(previous=>[...new Set([...previous,...visibleUsers.map(u=>u.id)])]);
        } else {
            setSelectedUsers(previous=>previous.filter(id=>!visibleUsers.some(user=>user.id===id)));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedUsers.length === 0) return;
        if (!window.confirm(`Permanently delete ${selectedUsers.length} users? This will remove their Login Account AND Data across all sessions. This cannot be undone.`)) {
            return;
        }

        setIsDeleting(true);
        try {
            const bulkDeleteUsers = httpsCallable(functions, 'bulkDeleteUsers');
            const result = await bulkDeleteUsers({ uids: selectedUsers });
            setSelectedUsers([]); 
            setModal({ show: true, message: `Successfully deleted ${result.data.count} users.` });
        } catch (error) {
            console.error("Error deleting users:", error);
            setModal({ show: true, message: `Failed to delete users: ${error.message}` });
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading) return <div className="flex justify-center mt-4"><Spinner color="border-purple-500" /></div>;

    const showActionsColumn = roleToList === 'student' || (roleToList === 'admin' && onEditUser);

    return (
        <div className="mt-6 bg-white p-6 rounded-lg shadow">
            {managingStudent && (
                <AssignPapersModal student={managingStudent} onClose={() => setManagingStudent(null)} setModal={setModal} />
            )}

            {showBulkRemoveModal && (
                <BulkRemovePapersModal 
                    selectedUserIds={selectedUsers} 
                    users={users}
                    allPapers={allPapers}
                    onClose={() => setShowBulkRemoveModal(false)} 
                    setModal={setModal}
                    clearSelection={() => setSelectedUsers([])}
                />
            )}
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h3 className="text-xl font-semibold text-gray-800">{roleToList.charAt(0).toUpperCase() + roleToList.slice(1)} List</h3>
                
                {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                        {roleToList === 'student' && (
                            <button 
                                onClick={() => setShowBulkRemoveModal(true)}
                                className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 font-semibold text-sm flex items-center gap-2"
                            >
                                Remove Papers ({selectedUsers.length})
                            </button>
                        )}
                        <button 
                            onClick={handleBulkDelete} disabled={isDeleting}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-semibold text-sm flex items-center gap-2"
                        >
                            {isDeleting ? 'Deleting...' : `Delete Selected (${selectedUsers.length})`}
                        </button>
                    </div>
                )}
            </div>

            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg bg-gray-50">
                <label className="block text-sm font-medium text-gray-700">Search users
                    <input type="search" value={nameFilter} disabled={isDeleting} onChange={e=>setNameFilter(e.target.value)} className="mt-1 block w-full px-3 py-2 border rounded-md" placeholder="Name, email, phone or user ID"/>
                </label>
                {roleToList==='student'&&<label className="block text-sm font-medium text-gray-700">Filter by paper
                    <select value={paperFilter} disabled={isDeleting} onChange={e=>setPaperFilter(e.target.value)} className="mt-1 block w-full px-3 py-2 border rounded-md"><option value="">All papers</option><option value="__none__">No papers assigned</option>{[...allPapers].sort((a,b)=>String(a.name).localeCompare(String(b.name))).map(paper=><option key={paper.id} value={paper.id}>{paper.name}</option>)}</select>
                </label>}
                <label className="block text-sm font-medium text-gray-700">Sort users
                    <select value={sort} disabled={isDeleting} onChange={e=>setSort(e.target.value)} className="mt-1 block w-full px-3 py-2 border rounded-md"><option value="name-asc">Name A–Z</option><option value="name-desc">Name Z–A</option><option value="email">Email A–Z</option><option value="newest">Newest accounts first</option><option value="oldest">Oldest accounts first</option></select>
                </label>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-4">
                <span>{filteredUsers.length} matching / {users.length} users</span>
                <button disabled={isDeleting} className="text-purple-700 underline" onClick={()=>{setNameFilter('');setPaperFilter('');setSort('name-asc');setPage(0);setSelectedUsers([]);}}>Clear user filters</button>
                {selectedUsers.length>0&&<><span>{selectedUsers.length} selected across pages</span><button disabled={isDeleting} className="text-purple-700 underline" onClick={()=>setSelectedUsers([])}>Clear selection</button></>}
            </div>
            {loadError&&<p role="alert" className="text-red-700 mb-4">{loadError}</p>}

            <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm text-gray-600">
                    <thead className="bg-gray-100 uppercase font-semibold text-gray-700">
                        <tr>
                            <th className="px-4 py-3 w-10">
                                <input 
                                    type="checkbox" 
                                    onChange={handleSelectAll} 
                                    aria-label="Select users on this page" disabled={isDeleting} checked={visibleUsers.length>0 && visibleUsers.every(user=>selectedUsers.includes(user.id))}
                                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                />
                            </th>
                            <th className="px-4 py-3">User Details</th>
                            {showActionsColumn && <th className="px-4 py-3 text-right">Actions</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {visibleUsers.length > 0 ? visibleUsers.map(user => (
                            <tr key={user.id} className={`hover:bg-gray-50 transition ${selectedUsers.includes(user.id) ? 'bg-purple-50' : ''}`}>
                                <td className="px-4 py-3">
                                    <input 
                                        type="checkbox" 
                                        aria-label={`Select ${user.name || user.email || user.id}`} disabled={isDeleting} checked={selectedUsers.includes(user.id)}
                                        onChange={() => handleSelectUser(user.id)}
                                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="font-semibold text-gray-800 text-base">{user.name || 'No Name'}</span>
                                        <span className="text-gray-500">{user.email}</span>
                                        <span className="text-gray-400 text-xs font-medium">{user.phone || 'No Phone Number'}</span>
                                    </div>
                                </td>
                                {showActionsColumn && (
                                    <td className="px-4 py-3 text-right">
                                        {roleToList === 'student' && (
                                            <button 
                                                onClick={() => setManagingStudent(user)} 
                                                className="bg-gray-200 text-gray-700 text-xs font-semibold py-1 px-3 rounded hover:bg-gray-300"
                                            >
                                                Manage Papers
                                            </button>
                                        )}
                                        {roleToList === 'admin' && onEditUser && (
                                            <button 
                                                onClick={() => onEditUser(user)}
                                                className="bg-purple-100 text-purple-700 text-xs font-semibold py-1 px-3 border border-purple-200 rounded hover:bg-purple-200 transition-colors ml-2"
                                            >
                                                Edit Permissions
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={showActionsColumn ? 3 : 2} className="text-center py-6 text-gray-400">
                                    No users match the current filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm">
                <button className="px-3 py-2 border rounded-lg disabled:opacity-40" disabled={currentPage===0||isDeleting} onClick={()=>setPage(currentPage-1)}>Previous users</button>
                <span>Page {currentPage+1} of {pages}</span>
                <button className="px-3 py-2 border rounded-lg disabled:opacity-40" disabled={currentPage+1>=pages||isDeleting} onClick={()=>setPage(currentPage+1)}>Next users</button>
            </div>
        </div>
    );
};

export default UserList;